import { inject, observer } from 'mobx-react';
import React, { Component } from 'react';
import styled from 'styled-components';
import { Box, Button, Flex, Heading, Icon, Popover, Text } from 'core/components';
import { withRouter } from 'react-router-dom/cjs/react-router-dom';
import { Position } from '@blueprintjs/core';
import moment from 'moment';
import KentikAiBadge from 'core/components/KentikAiBadge';
import { NavButton } from './Navbar';

const UnreadIndicator = styled.div`
  position: absolute;
  top: -4px;
  right: -8px;
  border-radius: 50%;
  padding: 0 3px;
  min-width: 16px;
  height: 16px;
  background-color: ${(props) => props.theme.colors.danger};
  color: #fff;
  font-size: 10px;
  font-weight: 500;
  line-height: 16px;
  text-align: center;
  transition: all 0.2s ease-in-out;
  animation: changeColor 1.4s infinite;

  @keyframes changeColor {
    0% {
      background-color: ${(props) => props.theme.colors.danger};
      transform: scale(1);
    }
    50% {
      background-color: ${(props) => props.theme.colors.warning};
      transform: scale(1.4);
      font-weight: 700;
    }
    100% {
      background-color: ${(props) => props.theme.colors.danger};
      transform: scale(1);
    }
  }
`;

function fromNow(date) {
  const duration = moment(date).fromNow(true);
  return duration
    .replace(' seconds', 's')
    .replace(' minutes', 'm')
    .replace(' minute', 'm')
    .replace(' hours', 'h')
    .replace(' hour', 'h')
    .replace(' days', 'd')
    .replace(' day', 'd')
    .replace(' weeks', 'w')
    .replace(' week', 'w')
    .replace(' months', 'mo')
    .replace(' month', 'mo')
    .replace(' years', 'y')
    .replace(' year', 'y');
}

const NotificationItem = observer(({ notification, onNavigate }) => (
  <Button minimal textAlign="left" onClick={onNavigate} p="12px" pl="16px">
    <Flex justifyContent="space-between" width="100%">
      <Flex flexDirection="column" gap="4px" lineHeight="18px" width="85%">
        <Flex alignItems="center" gap="5px" style={{ alignSelf: 'flex-start' }}>
          <KentikAiBadge small />
          <Heading level={6} m={0}>
            {notification.title}
          </Heading>
        </Flex>

        <Text small muted>
          {notification.message || 'A drop has been detected in inbound external traffic. Click to investigate.'}
        </Text>
      </Flex>
      <Text small muted>
        {fromNow(notification.timestamp)}
      </Text>
    </Flex>
  </Button>
));

@withRouter
@inject('$app', '$basicNotifications')
@observer
export default class NotificationsButton extends Component {
  state = {
    isPopoverOpen: false
  };

  renderIcon = ({ iconSize }) => {
    const { $basicNotifications } = this.props;
    const unreadCount = $basicNotifications.notifications.length;

    return (
      <Box position="relative">
        <Icon icon="notifications" color="white.10" iconSize={iconSize} />
        {unreadCount > 0 && <UnreadIndicator>{unreadCount}</UnreadIndicator>}
      </Box>
    );
  };

  handleNavigate = (path, index) => {
    const { $basicNotifications, history } = this.props;
    history.push(path);
    $basicNotifications.removeNotification(index);
  };

  handleTogglePopover = () => {
    this.setState((prevState) => ({ isPopoverOpen: !prevState.isPopoverOpen }));
  };

  render() {
    const { $basicNotifications } = this.props;
    const { isPopoverOpen } = this.state;
    const { notifications } = $basicNotifications;

    const hasNotifications = notifications.length > 0;

    return (
      <Popover
        minimal={false}
        isOpen={isPopoverOpen}
        position={Position.BOTTOM_LEFT}
        target={<NavButton onClick={this.handleTogglePopover} icon={this.renderIcon} minWidth={36} minimal />}
        content={
          <Flex width={425} gap={1} flexDirection="column" height={hasNotifications ? 450 : 'auto'}>
            <Flex justifyContent="space-between" alignItems="center" p={2} pb={0} pr="10px">
              <Heading m={0} level={5} flex={1}>
                Inbox
              </Heading>
              <Flex alignItems="center" gap={0}>
                {hasNotifications && (
                  <Button minimal icon="saved" onClick={() => $basicNotifications.clearNotifications()} />
                )}
                <Button minimal icon="cross" onClick={this.handleTogglePopover} />
              </Flex>
            </Flex>
            <Flex flexDirection="column">
              {notifications.length === 0 ? (
                <Box px={2} pb={2}>
                  <Text muted>No notifications</Text>
                </Box>
              ) : (
                notifications.map((notification, index) => (
                  <NotificationItem
                    key={notification.id}
                    onNavigate={() => this.handleNavigate(notification.path, index)}
                    notification={notification}
                  />
                ))
              )}
            </Flex>
          </Flex>
        }
      />
    );
  }
}
